import React, { useState, useEffect } from 'react';
import { getUserShareLinks, deleteShareLink } from '../services/api';
import { Share2, Trash2, ExternalLink, Clock, Globe, Copy, RefreshCw, CheckCircle2, Lock, Download, MessageSquare, Settings } from 'lucide-react';
import { useDataStore } from '../store/useDataStore';
import ShareDialog from './UI/ShareDialog';

export const SharesManagerView: React.FC = () => {
  const [shares, setShares] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [showPasswordId, setShowPasswordId] = useState<string | null>(null);  // 跟踪哪个分享链接正在显示密码
  const [editingShareId, setEditingShareId] = useState<string | null>(null);  // 正在编辑的分享ID
  const { userUuid, data, initData } = useDataStore();

  const loadShares = async () => {
    try {
      console.log('[SharesManagerView] loadShares 被调用, userUuid:', userUuid);
      setLoading(true);
      
      // 如果 userUuid 不存在，尝试初始化数据
      if (!userUuid) {
        console.log('[SharesManagerView] userUuid 不存在，尝试初始化数据...');
        try {
          await initData();
          // 重新获取 userUuid
          const { userUuid: newUserUuid } = useDataStore.getState();
          if (!newUserUuid) {
            console.warn('[SharesManagerView] 初始化后仍然没有 userUuid');
            setLoading(false);
            return;
          }
          console.log('[SharesManagerView] 初始化完成，新的 userUuid:', newUserUuid);
        } catch (initError) {
          console.error('[SharesManagerView] 初始化数据失败:', initError);
          setLoading(false);
          return;
        }
      }
      
      // 再次获取 userUuid（可能已经更新）
      const currentUserUuid = useDataStore.getState().userUuid;
      if (!currentUserUuid) {
        console.warn('[SharesManagerView] 仍然没有 userUuid，无法加载分享链接');
        setLoading(false);
        return;
      }
      
      console.log('[SharesManagerView] 开始请求分享链接，userUuid:', currentUserUuid);
      const response = await getUserShareLinks(currentUserUuid);
      console.log('[SharesManagerView] API 响应:', response);
      
      if (response.success && response.data) {
        // 确保数据格式正确，兼容不同的字段名，并从本地数据中获取项目标题
        const formattedShares = response.data.map((share: any) => {
          const shareId = share.share_id || share.shareId;
          const projectUuid = share.project_uuid || share.projectUuid;
          
          // 从本地项目数据中查找项目标题
          let projectTitle = '未命名画布';
          if (projectUuid && data.projects) {
            const project = data.projects.find((p: any) => p.uuid === projectUuid || p.id === projectUuid);
            if (project) {
              projectTitle = project.name || (project as any).title || '未命名画布';
            }
          }
          
          return {
            share_id: shareId,
            project_uuid: projectUuid,
            project_title: share.project_title || share.projectTitle || projectTitle,
            created_at: share.created_at || share.createdAt,
            expires_at: share.expires_at || share.expiresAt,
            allow_download: share.allow_download !== undefined ? share.allow_download : (share.allowDownload !== undefined ? share.allowDownload : true),
            message: share.message,
            has_password: share.has_password !== undefined ? share.has_password : (share.hasPassword !== undefined ? share.hasPassword : false),
            password: share.password || null,  // 保存密码用于显示
            lock_interface: share.lock_interface !== undefined ? share.lock_interface : (share.lockInterface !== undefined ? share.lockInterface : true)
          };
        });
        setShares(formattedShares);
        console.log('[SharesManagerView] 成功加载分享链接，数量:', formattedShares.length);
      } else {
        console.warn('[SharesManagerView] API 返回失败或数据为空:', response);
        setShares([]);
      }
    } catch (error) {
      console.error('[SharesManagerView] 加载分享链接失败:', error);
      setShares([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('[SharesManagerView] useEffect 触发，userUuid:', userUuid);
    loadShares();
  }, [userUuid]);

  const handleDelete = async (shareId: string) => {
    if (!confirm('确定要取消这个分享吗？链接将失效。')) return;
    
    try {
      if (!userUuid) return;
      await deleteShareLink(userUuid, shareId);
      loadShares();
    } catch (error) {
      alert('删除失败');
    }
  };

  const handleCopy = (shareId: string) => {
    const url = `${window.location.origin}/share/${shareId}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedId(shareId);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  // 如果没有 userUuid，显示提示
  if (!userUuid && !loading) {
    return (
      <div className="p-6 md:p-10 max-w-7xl mx-auto h-full overflow-y-auto custom-scrollbar flex items-center justify-center">
        <div className="text-center py-32 bg-white/50 rounded-[40px] border-4 border-dashed border-pink-100">
          <div className="w-24 h-24 bg-pink-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <Globe size={48} className="text-pink-200" />
          </div>
          <p className="text-gray-500 font-bold text-xl mb-2">用户未登录</p>
          <p className="text-gray-400 text-sm">
            请先登录或创建用户账户
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto h-full overflow-y-auto custom-scrollbar">
      <div className="flex justify-between items-start mb-10">
        <div>
          <h1 className="text-3xl font-black text-gray-800 mb-2 tracking-tight">我的分享</h1>
          <p className="text-gray-400 font-medium flex items-center gap-2">
            <Globe size={18} className="text-blue-400" />
            管理你发布到云端的画布链接
            {userUuid && (
              <span className="text-xs text-gray-300 ml-2">({userUuid.substring(0, 8)}...)</span>
            )}
          </p>
        </div>
        <button 
          onClick={() => {
            console.log('[SharesManagerView] 刷新按钮被点击');
            loadShares();
          }}
          disabled={loading}
          className="p-3 bg-white text-gray-600 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-all active:scale-95 disabled:opacity-50"
          title="刷新列表"
        >
          <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {loading && shares.length === 0 ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#ff5c5c] border-t-transparent"></div>
        </div>
      ) : shares.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {shares.map((share) => (
            <div key={share.share_id} className="group bg-white rounded-[32px] p-6 shadow-[0_8px_30px_rgb(0,0,0,0.02)] border border-transparent hover:border-pink-100 flex flex-col hover:shadow-[0_20px_40px_rgba(255,92,92,0.08)] transition-all duration-300">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center text-blue-500 shadow-sm">
                  <Share2 size={24} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-gray-800 truncate group-hover:text-[#ff5c5c] transition-colors">{share.project_title || '未命名画布'}</h3>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingShareId(share.share_id);
                      }}
                      className="p-1.5 text-gray-400 hover:text-blue-500 transition-colors rounded-lg hover:bg-blue-50"
                      title="设置"
                    >
                      <Settings size={16} />
                    </button>
                  </div>
                  <div className="flex items-center text-xs text-gray-400 gap-2 mt-1 flex-wrap">
                    <div className="flex items-center gap-1">
                      <Clock size={12} />
                      <span>{new Date(share.created_at).toLocaleDateString('zh-CN', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                    </div>
                    {share.has_password && (
                      <div 
                        className="flex items-center gap-1 text-orange-500 cursor-pointer hover:text-orange-600 transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowPasswordId(showPasswordId === share.share_id ? null : share.share_id);
                        }}
                        title={showPasswordId === share.share_id ? '点击隐藏密码' : '点击显示密码'}
                      >
                        <Lock size={12} />
                        <span>
                          {showPasswordId === share.share_id && share.password 
                            ? `密码: ${share.password}` 
                            : '密码保护'}
                        </span>
                      </div>
                    )}
                    {share.allow_download && (
                      <div className="flex items-center gap-1 text-green-500">
                        <Download size={12} />
                        <span>允许下载</span>
                      </div>
                    )}
                    {share.expires_at && new Date(share.expires_at) > new Date() && (
                      <div className="flex items-center gap-1 text-blue-500">
                        <span>到期: {new Date(share.expires_at).toLocaleDateString('zh-CN', { month: 'short', day: 'numeric' })}</span>
                      </div>
                    )}
                    {share.expires_at && new Date(share.expires_at) <= new Date() && (
                      <div className="flex items-center gap-1 text-red-500">
                        <span>已过期</span>
                      </div>
                    )}
                  </div>
                  {share.message && (
                    <div className="flex items-start gap-2 mt-2 text-xs text-gray-500 bg-gray-50 rounded-lg p-2">
                      <MessageSquare size={14} className="mt-0.5 flex-shrink-0" />
                      <span className="line-clamp-2">{share.message}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="relative group/link">
                <div className="bg-gray-50 rounded-2xl p-4 pr-12 mb-6 select-all font-mono text-xs text-gray-500 break-all border border-gray-100 group-hover/link:bg-blue-50/30 transition-colors">
                  {`${window.location.origin}/share/${share.share_id}`}
                </div>
                <button 
                  onClick={() => handleCopy(share.share_id)}
                  className="absolute right-3 top-3 p-2 text-gray-400 hover:text-blue-500 transition-colors"
                  title="复制链接"
                >
                  {copiedId === share.share_id ? <CheckCircle2 size={18} className="text-green-500" /> : <Copy size={18} />}
                </button>
              </div>

              <div className="flex gap-3 mt-auto">
                <a 
                  href={`/share/${share.share_id}`} 
                  target="_blank" 
                  rel="noreferrer"
                  className="flex-[2] bg-[#ff5c5c] hover:bg-[#ff4646] text-white py-3 rounded-2xl font-bold flex items-center justify-center gap-2 transition-all shadow-[0_8px_16px_rgba(255,92,92,0.2)] hover:shadow-[0_12px_24px_rgba(255,92,92,0.3)] hover:-translate-y-0.5 active:translate-y-0"
                >
                  <ExternalLink size={18} />
                  <span>访问画布</span>
                </a>
                <button 
                  onClick={() => handleDelete(share.share_id)}
                  className="flex-1 bg-gray-50 hover:bg-red-50 text-gray-400 hover:text-red-500 py-3 rounded-2xl font-bold flex items-center justify-center transition-all border border-transparent hover:border-red-100"
                  title="取消分享"
                >
                  <Trash2 size={20} />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-32 bg-white/50 rounded-[40px] border-4 border-dashed border-pink-100 animate-in fade-in duration-700">
          <div className="w-24 h-24 bg-pink-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <Share2 size={48} className="text-pink-200" />
          </div>
          <p className="text-gray-500 font-bold text-xl">暂无分享链接</p>
          <p className="text-gray-400 text-sm mt-3 max-w-xs mx-auto">
            在画布编辑页面点击“分享”按钮<br />即可将您的作品发布到云端
          </p>
        </div>
      )}

      {/* 分享设置对话框 */}
      {editingShareId && (
        <ShareDialog
          shareId={editingShareId}
          projectUuid={shares.find(s => s.share_id === editingShareId)?.project_uuid}
          onClose={() => {
            setEditingShareId(null);
            loadShares(); // 刷新列表
          }}
        />
      )}
    </div>
  );
};
